import { useState, useEffect, useCallback } from "react";
import { useLocation, useHistory, useParams } from "react-router-dom";
import { capitalize, get, isEmpty, maxBy, startCase } from "lodash";
import { Container, Row, Col, Breadcrumb, Button, Form } from "react-bootstrap";
import EventService from "service/eventService";
import {
    getActuatorCommand,
    getDeviceListByGatewayId,
    updateActuatorCommand,
} from "service/gatewayService";
import ContentWrapper from "components/content-wrapper/ContentWrapper";
import "assets/css/event.css";
import { ActionType, MOTOR_CONTROLS } from "constant";
import {
    NEW_ACTION_MESSAGE,
    UPDATE_ACTION_MESSAGE,
    ACTION_FAIL_MESSAGE,
} from "constant/messages";
import {
    isHttpSuccess,
    isFourWiredMotorControllerApp,
    getSensorFromDevices,
    formatValueWithUnit,
} from "utils/functions";
import { ResponseDto } from "types";
import { ActuatorCommandMode, Actuator } from "types/Actuator";
import {
    ActuatorCommandPatchMode,
    EventActionActuatorStatus,
} from "generated/models";
import { getDisplayedActuatorMode } from "pages/events/EventDetails";
import { handleAPIResp } from "components/events/ActionDetail/handleActionApiUtil";
import { useModal } from "components/modals/ModalTemplate";
import { isLoRaWanGateway_ByGatewayId } from "utils/gatewayFunctions";
import ActuatorConfigurationModal from "components/modals/ActuatorConfigurationModal";
import styled from "@emotion/styled";
import { BlueRadio } from "components/common";

type ActuatorFormData = Actuator & { pendingAutoChange?: boolean };

const ActionDetailBlock = styled.div`
    background: #3a4763;
    border-radius: 5px;
    padding: 14px 24px;
    display: flex;
    gap: 5px;
    flex-direction: column;
`;

const getDeviceInfo = async ({
    gateway_id,
    device_id,
    said,
}: {
    gateway_id: string;
    device_id: string;
    said: string;
}) => {
    const deviceList: any = await getDeviceListByGatewayId(gateway_id);

    return getSensorFromDevices(deviceList, {
        gateway_id,
        device_id,
        said,
    });
};

const getGatewayBusLabel = (gatewayId: string, busNumber: number) => {
    return isLoRaWanGateway_ByGatewayId(gatewayId)
        ? "LoRaWAN"
        : `LDSBUS ${busNumber}`;
};

const ActuatorConfigurationInfo = ({
    isFourWiredMotor,
    mode,
    isAutoControl,
    onChangeToAuto,
    isChangingToAuto,
    activateValue,
    deactivateValue,
    unit,
}: {
    isFourWiredMotor: boolean;
    mode: ActuatorCommandMode;
    isAutoControl: boolean;
    onChangeToAuto: () => void;
    isChangingToAuto: boolean;
    // For ANALOG mode only
    activateValue?: number;
    deactivateValue?: number;
    unit?: string;
}) => {
    const modeLabel = getDisplayedActuatorMode(mode);

    const formatValue = (value?: number, unit?: string) =>
        typeof value === "number" && !Number.isNaN(value)
            ? formatValueWithUnit(value, unit ?? "")
            : value;

    const renderControlMode = () => (
        <div>
            Control:{" "}
            <span className="text-primary-blue-4 ml-1">
                {capitalize(
                    isAutoControl
                        ? ActuatorCommandMode.AUTO
                        : ActuatorCommandMode.MANUAL
                )}
            </span>
            {!isAutoControl && (
                <button
                    type="button"
                    onClick={onChangeToAuto}
                    className="link-button ml-2"
                    disabled={isChangingToAuto}
                >
                    Change to Auto
                </button>
            )}
        </div>
    );

    if (mode === ActuatorCommandMode.ANALOG) {
        return (
            <>
                <div>
                    Activation Voltage:{" "}
                    <span className="text-primary-blue-4 ml-1">
                        {formatValue(activateValue, unit)}
                    </span>
                </div>
                <div>
                    Deactivation Voltage:{" "}
                    <span className="text-primary-blue-4 ml-1">
                        {formatValue(deactivateValue, unit)}
                    </span>
                </div>
                {renderControlMode()}
            </>
        );
    }

    return (
        <>
            {!isFourWiredMotor && modeLabel && (
                <div>
                    Mode:{" "}
                    <span className="text-primary-blue-4 ml-1">
                        {modeLabel}
                    </span>
                </div>
            )}
            {renderControlMode()}
        </>
    );
};

const TriggerActuator = () => {
    const location: any = useLocation();
    const history: any = useHistory();
    const params: any = useParams();
    const selectedActuatorDetails = get(
        location,
        "state.selectedActuatorDetails",
        {}
    );
    const OFF = "off";
    const ON = "on";
    const [checkedOption, setCheckedOption] = useState(OFF);
    const [actuatorDetails, setActuatorDetails] =
        useState<ActuatorFormData | null>(null);

    const [actionDetails, setActionDetails]: any = useState({
        name: "",
        type: "",
        mode: "",
        auto: false,
        gatewayId: "",
        ldsuUUID: "",
        said: "",
        isFourWireMotor: false,
        bus: "",
        gatewayName: "",
        unit: "",
    });
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [ctrlOption, setCtrlOption] = useState(MOTOR_CONTROLS.FORWARD);
    const [isChangingToAuto, setIsChangingToAuto] = useState(false);

    const { openModal, setModalShow, modal } = useModal();

    const backToEventPage = useCallback(() => {
        history.push(`/event-details/${params.eventId}`);
    }, [params.eventId, history]);

    const isFourWiredMotor = selectedActuatorDetails.obj
        ? isFourWiredMotorControllerApp(
            selectedActuatorDetails.obj,
            selectedActuatorDetails.appId
        )
        : actionDetails.isFourWireMotor;

    useEffect(() => {
        if (params.actionId) {
            const fetchCurrentSensor = async () => {
                const response: any = await EventService.readOneAction(
                    params.eventId,
                    params.actionId
                );

                if (isHttpSuccess(response.status)) {
                    const { device_id, gateway_id, said, status, polarity } =
                        response.data.resource;

                    const actuatorRes: any = await getActuatorCommand(
                        gateway_id,
                        device_id,
                        said
                    );
                    const actuator = get(actuatorRes, "data", {});
                    const {
                        auto = false,
                        mode = "LEVEL",
                        name = "",
                    } = actuator;

                    if (mode === ActuatorCommandMode.MOTOR) {
                        const motorControlMap = {
                            off: MOTOR_CONTROLS.PAUSE,
                            on: polarity
                                ? MOTOR_CONTROLS.FORWARD
                                : MOTOR_CONTROLS.REVERSE,
                        };
                        setCtrlOption(
                            motorControlMap[
                            status as keyof typeof motorControlMap
                            ]
                        );
                    }

                    const sensorInfo = await getDeviceInfo({
                        gateway_id,
                        device_id,
                        said,
                    });

                    setIsPageLoading(false);

                    setActionDetails({
                        name,
                        auto,
                        said,
                        mode,
                        type: sensorInfo?.sensor_type,
                        gatewayId: gateway_id,
                        ldsuUUID: device_id,
                        isFourWireMotor: isFourWiredMotorControllerApp(
                            sensorInfo?.OBJ,
                            sensorInfo?.APPID
                        ),
                        bus: sensorInfo?.bus,
                        gatewayName: sensorInfo?.gatewayName,
                        unit: sensorInfo?.unit,
                    });

                    setActuatorDetails(actuator);

                    if (status === "on") {
                        setCheckedOption(ON);
                    }
                } else {
                    setIsPageLoading(false);
                    openModal({
                        key: "action_actuator_error",
                        modalType: "err",
                        modalContent: response.data.description,
                        okAction: () => {
                            backToEventPage();
                        },
                    });
                }
            };
            fetchCurrentSensor();
        } else {
            // Create mode
            const fetchActuator = async () => {
                const response: any = await getActuatorCommand(
                    selectedActuatorDetails.gatewayId,
                    selectedActuatorDetails.ldsuUUID,
                    selectedActuatorDetails.said
                );

                if (isHttpSuccess(response.status)) {
                    const { data: actuator } = response;

                    setActuatorDetails(actuator);

                    const deviceInfo = await getDeviceInfo({
                        gateway_id: selectedActuatorDetails.gatewayId,
                        device_id: selectedActuatorDetails.ldsuUUID,
                        said: selectedActuatorDetails.said,
                    });

                    setActionDetails({
                        gatewayId: selectedActuatorDetails.gatewayId,
                        ldsuUUID: selectedActuatorDetails.ldsuUUID,
                        bus: deviceInfo?.bus,
                        gatewayName: deviceInfo?.gatewayName,
                        unit: deviceInfo?.unit,
                    });

                    setIsPageLoading(false);
                } else {
                    setIsPageLoading(false);
                    openModal({
                        key: "action_actuator_error",
                        modalType: "err",
                        modalContent: response.data.description,
                        okAction: () => {
                            backToEventPage();
                        },
                    });
                }
            };
            fetchActuator();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        params.actionId,
        params.eventId,
        selectedActuatorDetails.gatewayId,
        selectedActuatorDetails.ldsuUUID,
        selectedActuatorDetails.said,
    ]);

    const closeAction = () => {
        history.push(`/event-details/${params.eventId}`);
    };

    const transformPayload = async () => {
        const { gatewayId, ldsuUUID, said } = params.actionId
            ? actionDetails
            : selectedActuatorDetails;
        const value: EventActionActuatorStatus =
            checkedOption === OFF ? "off" : "on";

        const payload: any = {
            type_: ActionType.ACTUATOR,
            resource: {
                said: said,
                device_id: ldsuUUID,
                gateway_id: gatewayId,
                status: value,
            },
        };

        if (isFourWiredMotor) {
            if (ctrlOption === MOTOR_CONTROLS.PAUSE) {
                payload.resource.status = "off";
            } else {
                if (ctrlOption === MOTOR_CONTROLS.FORWARD) {
                    payload.resource.polarity = true;
                } else if (ctrlOption === MOTOR_CONTROLS.REVERSE) {
                    payload.resource.polarity = false;
                }
                payload.resource.status = "on";
            }
        }

        if (!params.actionId) {
            const { data } = await EventService.readOneEvent(params.eventId);
            const { actions = [] } = data;
            const latestAction: any = maxBy(actions, "order");
            const max = latestAction?.order ?? 0;

            return {
                ...payload,
                order: max + 1,
            };
        }

        return payload;
    };

    const handleButtonAction = async () => {
        let response: ResponseDto<any>;

        if (!actuatorDetails) {
            return;
        }

        if (actuatorDetails?.pendingAutoChange) {
            const { gatewayId, ldsuId, said } = getActuatorIdentity();

            if (
                !gatewayId ||
                !ldsuId ||
                said === undefined ||
                said === null ||
                said === ""
            ) {
                return;
            }

            setIsChangingToAuto(true);
            try {
                const configResponse = await updateActuatorCommand(
                    gatewayId,
                    ldsuId,
                    said,
                    {
                        auto: true,
                        mode: actuatorDetails.mode as ActuatorCommandPatchMode,
                        polarity: actuatorDetails.polarity,
                        timing: actuatorDetails.timing,
                        cycle_count: actuatorDetails.cycle_count,
                        deactivate: actuatorDetails.deactivate,
                        activate_value: actuatorDetails.activate_value,
                        deactivate_value: actuatorDetails.deactivate_value,
                    }
                );

                if (
                    !isHttpSuccess(configResponse.status) ||
                    !configResponse.data
                ) {
                    openModal({
                        key: "change_to_auto_error",
                        modalType: "err",
                        modalContent: get(
                            configResponse,
                            "data.description",
                            ACTION_FAIL_MESSAGE
                        ),
                    });
                    return;
                }

                setActuatorDetails(configResponse.data);
            } finally {
                setIsChangingToAuto(false);
            }
        }

        const payload = await transformPayload();

        if (params.actionId) {
            response = await EventService.updateAction(
                params.eventId,
                params.actionId,
                payload
            );
        } else {
            response = await EventService.createAction(params.eventId, payload);
        }

        const successMessage = params.actionId
            ? UPDATE_ACTION_MESSAGE
            : NEW_ACTION_MESSAGE;

        handleAPIResp({
            response,
            successMessage,
            defaultErrorMessage: "Unable to save the Trigger Actuator action",
            backToEventPage: closeAction,
        });
    };

    const renderActuatorInfo = () => {
        if (selectedActuatorDetails || !isEmpty(actionDetails)) {
            let name: any;
            let type: any;

            name =
                selectedActuatorDetails?.name ||
                actionDetails.name ||
                actuatorDetails?.name;
            type = selectedActuatorDetails?.type || actionDetails.type;

            const busLabel = getGatewayBusLabel(
                actionDetails.gatewayId,
                actionDetails.bus
            );

            return (
                <div className="form-box mb-3">
                    <h5 className="mb-4">Actuator</h5>
                    <ActionDetailBlock>
                        <h5>{name}</h5>
                        <div className="text-primary-blue-4">
                            {actionDetails.gatewayName} - {busLabel}
                        </div>
                        <div className="text-primary-blue-4">
                            LDSU UUID: {actionDetails.ldsuUUID}
                        </div>
                        <div className="text-primary-blue-4">
                            {startCase(type)}
                        </div>
                    </ActionDetailBlock>
                </div>
            );
        }
    };

    const openEditConfigurationModal = () => {
        let gatewayId: string = "";
        let ldsuId: string = "";
        let said: string = "";
        const isEditMode = !!params.actionId;

        if (isEditMode) {
            gatewayId = actionDetails?.gatewayId;
            ldsuId = actionDetails?.ldsuUUID;
            said = actionDetails?.said;
        } else {
            gatewayId = selectedActuatorDetails?.gatewayId;
            ldsuId = selectedActuatorDetails?.ldsuUUID;
            said = selectedActuatorDetails?.said;
        }

        if (gatewayId === "" || ldsuId === "" || said === "") {
            return;
        }

        const handleActuatorConfigSuccess = (actuatorCommand: Actuator) => {
            setActuatorDetails((prev) =>
                prev?.pendingAutoChange
                    ? {
                        ...actuatorCommand,
                        auto: true,
                        pendingAutoChange: true,
                    }
                    : actuatorCommand
            );
            setModalShow(false);
        };

        const modalContent = () => (
            <ActuatorConfigurationModal
                gatewayId={gatewayId}
                ldsuId={ldsuId}
                said={said}
                onSuccess={handleActuatorConfigSuccess}
            />
        );
        openModal({
            width: 720,
            key: "edit_actuator_configuration",
            modalType: "content",
            modalTitle: "Edit Configuration",
            modalContent,
        });
    };

    const getActuatorIdentity = () => {
        if (params.actionId) {
            return {
                gatewayId: actionDetails?.gatewayId,
                ldsuId: actionDetails?.ldsuUUID,
                said: actionDetails?.said,
            };
        }

        return {
            gatewayId: selectedActuatorDetails?.gatewayId,
            ldsuId: selectedActuatorDetails?.ldsuUUID,
            said: selectedActuatorDetails?.said,
        };
    };

    const handleChangeToAuto = () => {
        setActuatorDetails((prev) =>
            prev ? { ...prev, auto: true, pendingAutoChange: true } : null
        );
        setActionDetails((prev: any) => ({
            ...prev,
            auto: true,
        }));
    };

    return (
        <ContentWrapper isLoading={isPageLoading}>
            <Container>
                <Row>
                    <Col sm="12" className="event-detail-head">
                        <h5 className="page-title overflow-text">
                            Trigger Actuator
                        </h5>
                    </Col>
                </Row>
                <Row>
                    <Col sm="12">
                        <Breadcrumb className="w-100">
                            <Breadcrumb.Item href="/events">
                                Events
                            </Breadcrumb.Item>
                            <Breadcrumb.Item
                                href={`/event-details/${params.eventId}`}
                            >
                                Event Details
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active>Action</Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>
                </Row>
                <Row className="condition-detail-box">
                    <Col>
                        {renderActuatorInfo()}
                        <div className="form-box mb-3">
                            <div className="w-100 d-flex justify-content-between align-items-center mb-3">
                                <h5 className="mb-0">Configuration</h5>
                                {!isFourWiredMotor && (
                                    <button
                                        onClick={() =>
                                            openEditConfigurationModal()
                                        }
                                        className="link-button"
                                    >
                                        Edit Configuration
                                    </button>
                                )}
                            </div>
                            <ActionDetailBlock>
                                {actuatorDetails?.mode && (
                                    <ActuatorConfigurationInfo
                                        isFourWiredMotor={isFourWiredMotor}
                                        mode={actuatorDetails.mode}
                                        isAutoControl={!!actuatorDetails?.auto}
                                        onChangeToAuto={handleChangeToAuto}
                                        isChangingToAuto={isChangingToAuto}
                                        activateValue={
                                            actuatorDetails?.activate_value
                                        }
                                        deactivateValue={
                                            actuatorDetails?.deactivate_value
                                        }
                                        unit={actionDetails.unit}
                                    />
                                )}
                            </ActionDetailBlock>
                        </div>
                        <div className="form-box mb-3">
                            <h5 className="mb-4">Action</h5>
                            {isFourWiredMotor ? (
                                <Form.Row>
                                    {Object.values(MOTOR_CONTROLS).map(
                                        (item) => (
                                            <Col sm={2} key={item}>
                                                <BlueRadio
                                                    name="mode"
                                                    id={item}
                                                    custom
                                                    type="radio"
                                                    onChange={() => {
                                                        setCtrlOption(item);
                                                    }}
                                                    checked={
                                                        ctrlOption === item
                                                    }
                                                    label={
                                                        <span className="ml-2">
                                                            {item.capitalize()}
                                                        </span>
                                                    }
                                                />
                                            </Col>
                                        )
                                    )}
                                </Form.Row>
                            ) : (
                                <Form.Row>
                                    <Col sm={2}>
                                        <BlueRadio
                                            name="mode"
                                            id={ON}
                                            custom
                                            type="radio"
                                            onChange={() => {
                                                setCheckedOption(ON);
                                            }}
                                            checked={checkedOption === ON}
                                            label={
                                                <span className="ml-2">
                                                    Activate
                                                </span>
                                            }
                                        />
                                    </Col>
                                    <Col sm={2}>
                                        <BlueRadio
                                            name="mode"
                                            id={OFF}
                                            custom
                                            type="radio"
                                            onChange={() => {
                                                setCheckedOption(OFF);
                                            }}
                                            checked={checkedOption === OFF}
                                            label={
                                                <span className="ml-2">
                                                    Deactivate
                                                </span>
                                            }
                                        />
                                    </Col>
                                </Form.Row>
                            )}
                        </div>
                        <Button
                            variant="secondary"
                            className="mr-2"
                            onClick={closeAction}
                        >
                            CANCEL
                        </Button>
                        <Button
                            variant="primary"
                            className="pl-4 pr-4"
                            onClick={handleButtonAction}
                        >
                            {params.actionId ? "UPDATE" : "ADD"}
                        </Button>
                    </Col>
                </Row>
            </Container>

            {modal}
        </ContentWrapper>
    );
};

export default TriggerActuator;
